import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from './firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { validateYesNoQuestion, validateMultipleChoiceQuestion } from './validation';
import type { YesNoQuestion, MultipleChoiceQuestion, Question } from './types';

function AdminQuestions(): React.ReactElement {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [newQuestionText, setNewQuestionText] = useState<string>('');
  const [newQuestionType, setNewQuestionType] = useState<'yes_no' | 'multiple_choice'>('yes_no');
  const [newQuestionOptions, setNewQuestionOptions] = useState<string>('');
  const [newQuestionCategory, setNewQuestionCategory] = useState<string>('');
  const [newQuestionClass, setNewQuestionClass] = useState<number>(1);
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async (): Promise<void> => {
      if (!user) return;

      const adminDocRef = doc(db, 'admins', user.uid);
      const adminDoc = await getDoc(adminDocRef);

      if (adminDoc.exists()) {
        setIsAdmin(true);
        await loadQuestions();
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    };

    checkAdmin();
  }, [user]);

  // Load all questions
  const loadQuestions = async (): Promise<void> => {
    try {
      const questionsSnapshot = await getDocs(collection(db, 'questions'));
      const questionsData: Question[] = questionsSnapshot.docs.map(doc => {
        const data = doc.data();
        const type = data.type || 'yes_no';

        if (type === 'multiple_choice') {
          validateMultipleChoiceQuestion(doc.id, data);

          return {
            id: doc.id,
            text: data.text,
            type: 'multiple_choice',
            options: data.options || [],
            category: Array.isArray(data.category) ? data.category : (data.category ? [data.category] : []),
            class: data.class || 1,
          } as MultipleChoiceQuestion;
        } else {
          validateYesNoQuestion(doc.id, data);

          return {
            id: doc.id,
            text: data.text,
            type: 'yes_no',
            category: Array.isArray(data.category) ? data.category : (data.category ? [data.category] : []),
            class: data.class || 1,
          } as YesNoQuestion;
        }
      });
      setQuestions(questionsData);
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  };

  // Add new question
  const handleAddQuestion = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!newQuestionText.trim()) return;
    if (newQuestionType === 'multiple_choice' && !newQuestionOptions.trim()) {
      setMessage('Please enter options for multiple choice question');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      const questionData: any = {
        text: newQuestionText.trim(),
        type: newQuestionType,
        category: newQuestionCategory.trim().split(',').map(c => c.trim()).filter(c => c.length > 0),
        class: newQuestionClass,
        createdAt: serverTimestamp(),
      };

      if (newQuestionType === 'multiple_choice') {
        // Parse options from comma-separated string
        questionData.options = newQuestionOptions
          .split(',')
          .map(opt => opt.trim())
          .filter(opt => opt.length > 0);
      }

      await addDoc(collection(db, 'questions'), questionData);
      setNewQuestionText('');
      setNewQuestionOptions('');
      setNewQuestionCategory('');
      setNewQuestionClass(1);
      setNewQuestionType('yes_no');
      setMessage('Question added successfully');
      await loadQuestions();
    } catch (error) {
      console.error('Error adding question:', error);
      setMessage('Failed to add question');
    }
    setSaving(false);
    setTimeout(() => setMessage(''), 3000);
  };

  // Duplicate question - populate form with question data
  const handleDuplicateQuestion = (question: Question): void => {
    setNewQuestionText(question.text);
    setNewQuestionType(question.type);
    setNewQuestionCategory(question.category.join(', '));
    setNewQuestionClass(question.class);
    if (question.type === 'multiple_choice') {
      setNewQuestionOptions(question.options.join(', '));
    } else {
      setNewQuestionOptions('');
    }
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Delete question
  const handleDeleteQuestion = async (questionId: string): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this question? This will also remove all user answers to this question.')) return;

    try {
      // Delete the question
      await deleteDoc(doc(db, 'questions', questionId));

      // Clean up answers from all users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const updatePromises = usersSnapshot.docs.map(async (userDoc) => {
        const userData = userDoc.data();
        if (userData.answers && userData.answers[questionId]) {
          // Remove this question's answer from user's answers
          const updatedAnswers = { ...userData.answers };
          delete updatedAnswers[questionId];

          await updateDoc(doc(db, 'users', userDoc.id), {
            answers: updatedAnswers
          });
        }
      });

      await Promise.all(updatePromises);

      setMessage('Question and all related answers deleted');
      await loadQuestions();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting question:', error);
      setMessage('Failed to delete question');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }

  if (!isAdmin) {
    return (
      <div style={{ padding: '20px' }}>
        <h2>Access Denied</h2>
        <p>You do not have permission to access this page.</p>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          Back to Profile
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <button
        onClick={() => navigate('/')}
        style={{
          padding: '10px 20px',
          fontSize: '16px',
          backgroundColor: '#2196F3',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          marginBottom: '20px'
        }}
      >
        Back to Profile
      </button>

      <h1>Admin - Manage Questions</h1>

      <div style={{ marginBottom: '30px', backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '8px' }}>
        <h2 style={{ marginTop: 0 }}>Add New Question</h2>
        <form onSubmit={handleAddQuestion}>
          <input
            type="text"
            value={newQuestionText}
            onChange={(e) => setNewQuestionText(e.target.value)}
            placeholder="Enter question text (e.g., Are you a cat person?)"
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              marginBottom: '10px',
            }}
          />

          <input
            type="text"
            value={newQuestionCategory}
            onChange={(e) => setNewQuestionCategory(e.target.value)}
            placeholder="Categories (comma-separated, optional)"
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              marginBottom: '10px',
            }}
          />

          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
              Class (1=most important, higher=less important):
            </label>
            <input
              type="number"
              min="1"
              value={newQuestionClass}
              onChange={(e) => setNewQuestionClass(Math.max(1, parseInt(e.target.value) || 1))}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                borderRadius: '4px',
                border: '1px solid #ccc',
              }}
            />
          </div>

          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
              Question Type:
            </label>
            <select
              value={newQuestionType}
              onChange={(e) => setNewQuestionType(e.target.value as 'yes_no' | 'multiple_choice')}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                borderRadius: '4px',
                border: '1px solid #ccc',
              }}
            >
              <option value="yes_no">Yes/No Question</option>
              <option value="multiple_choice">Multiple Choice</option>
            </select>
          </div>

          {newQuestionType === 'multiple_choice' && (
            <input
              type="text"
              value={newQuestionOptions}
              onChange={(e) => setNewQuestionOptions(e.target.value)}
              placeholder="Enter options separated by commas (e.g., 0, 1, 2, 3, 4 or more)"
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                marginBottom: '10px',
              }}
            />
          )}

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              type="submit"
              disabled={saving || !newQuestionText.trim()}
              style={{
                padding: '10px 20px',
                fontSize: '16px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: saving || !newQuestionText.trim() ? 'not-allowed' : 'pointer',
                opacity: saving || !newQuestionText.trim() ? 0.6 : 1,
              }}
            >
              {saving ? 'Adding...' : 'Add Question'}
            </button>
            {message && (
              <span style={{ color: message.includes('success') ? '#4CAF50' : '#f44336', fontSize: '14px' }}>
                {message}
              </span>
            )}
          </div>
        </form>
      </div>

      <div>
        <h2>Existing Questions ({questions.length})</h2>
        {questions.length === 0 ? (
          <p style={{ color: '#666' }}>No questions yet. Add your first question above.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {questions.map((question) => (
              <div
                key={question.id}
                style={{
                  padding: '15px',
                  backgroundColor: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <p style={{ margin: 0, fontSize: '16px' }}>{question.text}</p>
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#999' }}>
                    Class: {question.class} | Type: {question.type === 'multiple_choice' ? 'Multiple Choice' : 'Yes/No'} | Categories: {question.category.length > 0 ? question.category.join(', ') : 'None'} | ID: {question.id}
                  </p>
                  {question.type === 'multiple_choice' && (
                    <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>
                      Options: {question.options.join(', ')}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => handleDuplicateQuestion(question)}
                    style={{
                      padding: '8px 16px',
                      fontSize: '14px',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    Duplicate
                  </button>
                  <button
                    onClick={() => handleDeleteQuestion(question.id)}
                    style={{
                      padding: '8px 16px',
                      fontSize: '14px',
                      backgroundColor: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={() => navigate('/')}
        style={{
          padding: '10px 20px',
          fontSize: '16px',
          backgroundColor: '#2196F3',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          marginTop: '30px'
        }}
      >
        Back to Profile
      </button>
    </div>
  );
}

export default AdminQuestions;
